import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Globe, MapPin, Heart, MessageSquare, Send, Loader2, Image as ImageIcon, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SocialFeedPage = () => {
  const { user, golfer } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLocal, setIsLocal] = useState(false);
  const [postText, setPostText] = useState('');
  const [posting, setPosting] = useState(false);
  const [userLocation, setUserLocation] = useState(null);

  useEffect(() => { fetchPosts(); }, [isLocal]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (isLocal && userLocation) {
        params.append('lat', userLocation.latitude);
        params.append('lng', userLocation.longitude);
        params.append('radius', '50');
      }
      const res = await axios.get(`${API_URL}/social/feed?${params}`);
      setPosts(res.data.posts || []);
    } catch { setPosts([]); }
    finally { setLoading(false); }
  };

  const handleToggleLocal = async (checked) => {
    if (checked && !userLocation) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => {
            setUserLocation({ latitude: pos.coords.latitude, longitude: pos.coords.longitude });
            setIsLocal(true);
          },
          () => { toast.error('Location access denied. Showing global feed.'); setIsLocal(false); },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      } else { toast.error('Geolocation not supported'); }
    } else {
      setIsLocal(checked);
    }
  };

  const handlePost = async () => {
    if (!postText.trim()) return;
    if (postText.length > 500) { toast.error('Post must be 500 characters or less'); return; }
    setPosting(true);
    try {
      await axios.post(`${API_URL}/social/post`, { content: postText });
      setPostText('');
      toast.success('Posted!');
      fetchPosts();
    } catch (err) {
      if (err.response?.status === 429) toast.error('Brand accounts: 1 promo post per 60 days.');
      else toast.error('Failed to post');
    } finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    try {
      await axios.post(`${API_URL}/social/post/${postId}/like`);
      setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, likes: (p.likes || 0) + 1, liked: true } : p));
    } catch {}
  };

  return (
    <div className="space-y-6" data-testid="social-feed-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-2xl text-white">The Clubhouse</h1>
          <p className="text-cream/70 text-sm">Community feed</p>
        </div>
        <div className="flex items-center gap-3">
          <Globe className={`w-4 h-4 ${!isLocal ? 'text-gold' : 'text-cream/50'}`} />
          <Switch checked={isLocal} onCheckedChange={handleToggleLocal} data-testid="location-toggle" />
          <MapPin className={`w-4 h-4 ${isLocal ? 'text-gold' : 'text-cream/50'}`} />
          <span className="text-cream/70 text-xs">{isLocal ? 'Local' : 'Global'}</span>
        </div>
      </div>

      {/* Create Post */}
      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
              {golfer?.photo_url ? (
                <img src={golfer.photo_url} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                <User className="w-5 h-5 text-gold" />
              )}
            </div>
            <div className="flex-1">
              <Textarea value={postText} onChange={e => setPostText(e.target.value)}
                placeholder="What's on your mind, golfer?" maxLength={500} rows={3}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/40 resize-none mb-2"
                data-testid="create-post-input" />
              <div className="flex items-center justify-between">
                <span className="text-cream/40 text-xs">{postText.length}/500</span>
                <Button size="sm" className="btn-gold" onClick={handlePost} disabled={posting || !postText.trim()}
                  data-testid="submit-post-btn">
                  {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> Post</>}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feed */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 text-cream/50">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-display">No posts yet. Be the first!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {posts.map(post => (
            <Card key={post.post_id} className="bg-white/5 border-white/10" data-testid="feed-post">
              <CardContent className="p-4">
                <div className="flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center shrink-0 overflow-hidden">
                    {post.author_photo ? (
                      <img src={post.author_photo} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-gold font-bold">{post.author_name?.charAt(0) || '?'}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-white text-sm">{post.author_name || 'Member'}</span>
                      {post.author_city && (
                        <Badge variant="outline" className="text-cream/50 border-white/10 text-xs">
                          {post.author_city}, {post.author_state}
                        </Badge>
                      )}
                      <span className="text-cream/40 text-xs ml-auto">
                        {post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <p className="text-cream/90 text-sm whitespace-pre-wrap mb-3">{post.content}</p>
                    {post.image_url && (
                      <img src={post.image_url} alt="" className="rounded-sm max-h-64 w-full object-cover mb-3" />
                    )}
                    <div className="flex items-center gap-4">
                      <button onClick={() => handleLike(post.post_id)}
                        className={`flex items-center gap-1 text-sm ${post.liked ? 'text-gold' : 'text-cream/50 hover:text-gold'}`}
                        data-testid={`like-post-${post.post_id}`}>
                        <Heart className={`w-4 h-4 ${post.liked ? 'fill-gold' : ''}`} />
                        {post.likes || 0}
                      </button>
                      <span className="flex items-center gap-1 text-cream/50 text-sm">
                        <MessageSquare className="w-4 h-4" /> {post.comments || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SocialFeedPage;
